
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Home, Music, AlertCircle, ArrowLeft, RefreshCw, Bug, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import { toast } from "sonner";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState(0);
  const [loadingReattempt, setLoadingReattempt] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [diagnosticInfo, setDiagnosticInfo] = useState<Record<string, any>>({});

  useEffect(() => {
    // Collect diagnostic information
    const collectDiagnostics = () => {
      try {
        const info = {
          url: window.location.href,
          path: location.pathname,
          time: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer || 'No referrer',
          windowSize: `${window.innerWidth}x${window.innerHeight}`,
          connectionType: (navigator as any).connection ? 
            (navigator as any).connection.effectiveType : 'Unknown',
          hasServiceWorker: 'serviceWorker' in navigator,
          isReactDevToolsInstalled: (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__ ? true : false,
        };
        setDiagnosticInfo(info);
      } catch (error) {
        console.error('Error collecting diagnostics:', error);
      }
    };

    collectDiagnostics();

    // Log the attempted path for debugging purposes
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname,
      {
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        href: window.location.href
      }
    );
    
    // Show toast notification to explain the error
    toast.error("Page not found", {
      description: "The page you're looking for couldn't be found. This might be a temporary routing issue.",
      duration: 5000,
    });
  }, [location.pathname]);

  // Check if the path might be a typo of a legal page
  const mightBeLegalPage = location.pathname.includes('legal');
  const isDashboardRelated = location.pathname.includes('dashboard') || 
                           location.pathname.includes('calendar') || 
                           location.pathname.includes('marketing');

  // Strip any leading/trailing slashes for better matching
  const cleanPath = location.pathname.replace(/^\/+|\/+$/g, '');
  
  const handleRetry = () => {
    setLoadingReattempt(true);
    setAttempts(attempts + 1);
    
    // Attempt to navigate to the same page again - helps with temporary routing issues
    const currentPath = location.pathname;
    
    // Force a hard refresh if we've tried more than 3 times
    if (attempts >= 2) {
      toast.info("Performing a full page refresh...");
      setTimeout(() => {
        window.location.href = currentPath;
      }, 1000);
      return;
    }
    
    // Use navigate with replace and a small delay to give routing time to initialize
    toast.info("Retrying navigation...");
    setTimeout(() => {
      try {
        navigate(0); // This will refresh the current page
      } catch (error) {
        console.error("Navigation error:", error);
        // If navigation fails, try a direct page reload
        window.location.reload();
      } finally {
        setLoadingReattempt(false);
      }
    }, 1000);
  };

  const handleGoHome = () => {
    // Clear any stored route data that might be causing issues
    sessionStorage.removeItem('lastRoute');
    // Navigate to home with a reload to ensure clean state
    window.location.href = '/';
  };

  return (
    <Layout>
      <div className="flex items-center justify-center p-4 min-h-[80vh]">
        <div className="text-center max-w-md mx-auto animate-fade-in">
          <div className="mb-6">
            {mightBeLegalPage ? (
              <AlertCircle className="h-20 w-20 mx-auto text-amber-500 opacity-80" />
            ) : isDashboardRelated ? (
              <ArrowLeft className="h-20 w-20 mx-auto text-blue-500 opacity-80" />
            ) : (
              <Music className="h-20 w-20 mx-auto text-muted-foreground opacity-20" />
            )}
          </div>
          <h1 className="text-5xl font-bold mb-4">404</h1>
          
          {mightBeLegalPage ? (
            <div>
              <p className="text-xl text-muted-foreground mb-2">
                Looking for legal resources?
              </p>
              <p className="text-base text-muted-foreground mb-8">
                Our legal tools and resources can be found at the legal-tools page.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild className="gap-2">
                  <Link to="/legal-tools">
                    <AlertCircle className="h-4 w-4" />
                    <span>Go to Legal Tools</span>
                  </Link>
                </Button>
                <Button variant="outline" asChild className="gap-2" onClick={handleGoHome}>
                  <Link to="/">
                    <Home className="h-4 w-4" />
                    <span>Return to Home</span>
                  </Link>
                </Button>
              </div>
            </div>
          ) : isDashboardRelated ? (
            <div>
              <p className="text-xl text-muted-foreground mb-2">
                Page not found in dashboard
              </p>
              <p className="text-base text-muted-foreground mb-8">
                This dashboard page may have been moved or renamed. Please navigate from the main dashboard.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild className="gap-2">
                  <Link to="/dashboard">
                    <Home className="h-4 w-4" />
                    <span>Go to Dashboard</span>
                  </Link>
                </Button>
                <Button variant="outline" onClick={handleRetry} className="gap-2" disabled={loadingReattempt}>
                  <RefreshCw className={`h-4 w-4 ${loadingReattempt ? 'animate-spin' : ''}`} />
                  <span>{loadingReattempt ? 'Trying Again...' : 'Try Again'}</span>
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-xl text-muted-foreground mb-2">
                Oops! This page seems to have gone off-beat.
              </p>
              <p className="text-base text-muted-foreground mb-6">
                The page you're looking for doesn't exist or may have been moved.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild className="gap-2" onClick={handleGoHome}>
                  <Link to="/">
                    <Home className="h-4 w-4" />
                    <span>Return Home</span>
                  </Link>
                </Button>
                <Button variant="outline" onClick={handleRetry} className="gap-2" disabled={loadingReattempt}>
                  <RefreshCw className={`h-4 w-4 ${loadingReattempt ? 'animate-spin' : ''}`} />
                  <span>{loadingReattempt ? 'Trying Again...' : 'Try Again'}</span>
                </Button>
              </div>
            </div>
          )}
          
          <div className="mt-8 text-xs text-muted-foreground">
            <p>Current path: {location.pathname}</p>
            <p className="mt-1">Browser URL: {window.location.href}</p>
            {attempts > 0 && <p className="mt-1">Retry attempts: {attempts}</p>}
            
            <Collapsible 
              className="mt-4 border border-border rounded-md p-2"
              open={showDiagnostics}
              onOpenChange={setShowDiagnostics}
            >
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 w-full">
                  <Bug className="h-4 w-4" />
                  <span>Technical Details</span>
                  {showDiagnostics ? 
                    <ChevronUp className="h-4 w-4 ml-2" /> : 
                    <ChevronDown className="h-4 w-4 ml-2" />
                  }
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="text-left p-2">
                <div className="space-y-1 mt-2">
                  {Object.entries(diagnosticInfo).map(([key, value]) => (
                    <p key={key} className="break-all">
                      <span className="font-semibold">{key}:</span> {String(value)}
                    </p>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NotFound;
